import React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { PrimaryButton } from 'shared/components/button'
import ErrorMessageRhf from 'shared/components/error-message-rhf'
import FormInputRhf from 'shared/components/form-input-rhf'
import { Recaptcha } from 'shared/components/recaptcha'
import { useRestorePasswordForm } from '../hooks/use-restore-password-form'

interface RestorePasswordFormProps {
  onSuccess: () => void
  isSent: boolean
}

export const RestorePasswordForm = ({ onSuccess, isSent }: RestorePasswordFormProps) => {
  const { t } = useTranslation()
  const router = useRouter()

  const {
    handleSubmit,
    control,
    setValue,
    errors,
    isLoading,
    onSubmit,
    isRecaptchaError,
    toggleRecaptchaError,
    recaptchaRef,
  } = useRestorePasswordForm({
    onSuccess,
  })

  return (
    <div className="mx-auto my-16 max-w-[500px]">
      <div className="rounded-lg bg-background-secondary p-10 text-center shadow-authShadow">
        <h2 className="mb-4">
          {isSent ? t('resetting.check_email.title') : t('resetting.request.title')}
        </h2>
        <div className="flex flex-col gap-5">
          {isSent ? (
            <p className="mb-6 text-text-tertiary">{t('resetting.check_email.description')}</p>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <FormInputRhf
                required
                type="email"
                name="email"
                className="flex-auto text-[1.1rem] font-normal"
                label={t('authentication.email')}
                control={control}
                setValue={setValue}
                maxLength={180}
                rules={{ required: t('login.validation.email') }}
                error={errors.email?.message}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
              />
              {!isRecaptchaError && (
                <Controller
                  control={control}
                  rules={{ required: t('user.registration.captcha.verification') }}
                  name="token"
                  render={({ field, formState: { errors } }) => (
                    <div>
                      <Recaptcha
                        key={router.locale}
                        ref={recaptchaRef}
                        onTokenChange={field.onChange}
                        onError={toggleRecaptchaError}
                      />
                      <ErrorMessageRhf errors={errors} fieldName="token" />
                    </div>
                  )}
                />
              )}
              <ErrorMessageRhf errors={errors} fieldName="root" />
              <PrimaryButton isLoading={isLoading} type="submit" className="text-xl">
                {t('resetting.request.submit')}
              </PrimaryButton>
            </form>
          )}
        </div>
      </div>

      {isSent && (
        <Link className="mt-3 block text-brand-primary" href="/login">
          {t('resetting.check_email.back_to_login')}
        </Link>
      )}
    </div>
  )
}
